import { useState } from 'react'
import { useUserStore } from '~/entities/user'
import { formatDateDefault, formatTimeDefault } from '~/shared/lib/datetime'
import {
  Button,
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  LocalIcon,
  Text,
  Textarea,
} from '~/shared/ui'

interface SubmittedComment {
  notes: string
  time: number
}

interface TaggingCommentsProps {
  onSend: (notes: string) => void
  isPending: boolean
}

export function TaggingComments({ onSend, isPending }: TaggingCommentsProps) {
  const userInfo = useUserStore((state) => state.userInfo)
  const userName = [userInfo?.given_name, userInfo?.family_name].filter(Boolean).join(' ')
  const [notes, setNotes] = useState('')
  const [submittedComments, setSubmittedComments] = useState<SubmittedComment[]>([])

  const handleSend = () => {
    const trimmed = notes.trim()
    onSend(trimmed)
    setSubmittedComments((prev) => [...prev, { notes: trimmed, time: Date.now() }])
    setNotes('')
  }

  return (
    <div className="border-alert-error-content rounded border p-4">
      {submittedComments.length === 0 ? (
        <div className="flex flex-col gap-2">
          <CollapsibleRoot>
            <CollapsibleTrigger
              label="Comments"
              labelClassName="text-alert-error-content"
            />
            <CollapsibleContent>
              <div className="mt-3 flex flex-col gap-2">
                <Text
                  appearance="CAPTION"
                  className="text-sm"
                >
                  Why is this suggestion being corrected?
                  <span className="text-error-main">*</span>
                </Text>
                <div className="relative">
                  <Textarea
                    placeholder="Add your thoughts here."
                    className="h-[80px] resize-none"
                    filled
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                  <Button
                    className="absolute right-3 bottom-2"
                    size="small"
                    disabled={notes.length < 3 || isPending}
                    onClick={handleSend}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </CollapsibleRoot>
        </div>
      ) : (
        <CollapsibleRoot defaultOpen={false}>
          <CollapsibleTrigger
            label="Comments"
            labelClassName="text-alert-error-content"
            counter={submittedComments.length}
          />
          <CollapsibleContent>
            <div className="mt-3 flex flex-col gap-3">
              {submittedComments.map((comment) => {
                const date = new Date(comment.time)
                return (
                  <div
                    key={comment.time}
                    className="flex flex-col py-1"
                  >
                    <div className="flex items-start gap-1">
                      <div className="text-text-secondary flex items-center gap-2 whitespace-nowrap">
                        <Text
                          className="uppercase"
                          appearance="CAPTION"
                        >
                          {formatTimeDefault(comment.time)}
                        </Text>
                        <span className="bg-text-secondary h-1 w-1 rounded-full" />
                        <Text
                          className="uppercase"
                          appearance="CAPTION"
                        >
                          {formatDateDefault(date)}
                        </Text>
                        <LocalIcon
                          name="personOutlined"
                          size="16"
                        />
                      </div>
                      <Text appearance="CAPTION">{userName}</Text>
                    </div>
                    <div className="mt-2">
                      <Text appearance="CAPTION">{comment.notes}</Text>
                    </div>
                  </div>
                )
              })}
            </div>
          </CollapsibleContent>
          <div className="mt-3 flex flex-col gap-1">
            <Text
              appearance="BODY2"
              className="font-semibold"
            >
              Selection Validated!
            </Text>
            <Text
              appearance="CAPTION"
              className="text-sm"
            >
              Thanks for the insight! Your feedback has been logged to help train opti&apos;s logic
              for better CARC/RARC suggestions.
            </Text>
          </div>
        </CollapsibleRoot>
      )}
    </div>
  )
}
